// IND-004: tarjeta de cada fichero de la carga diaria (estado, fecha de los
// datos y qué se descartó y por qué). Solo describe el resultado: no cambia
// qué se importa ni cómo se calcula.
import type {
  DailyLoadRunDiscard,
  DailyLoadRunFile,
  DailyLoadRunResult,
} from '../services/industrialCostService';
import type {
  DirectMaterialEntryImportConfirmResponse,
  ImportConfirmDiscard,
  ImportedManufacturingOrderImportBlockingIssue,
  ImportedManufacturingOrderImportConfirmResponse,
  ProductionImportSummary,
  PurchaseOrderImportConfirmResponse,
} from '../types/industrialCost';
import { formatEsNumber } from './numberFormat';

export const DAILY_LOAD_SOURCES = [
  'production',
  'direct-inputs',
  'imported-manufacturing-orders',
  'purchase-orders',
] as const;

export const DAILY_LOAD_SOURCE_LABELS: Record<string, string> = {
  production: 'Producción',
  'direct-inputs': 'Albaranes',
  'imported-manufacturing-orders': 'OF pendientes',
  'purchase-orders': 'Compras pendientes',
};

export const DATA_DATE_KIND_LABELS: Record<string, string> = {
  count: 'fecha del recuento',
  production: 'último día con producción',
  file_modified: 'fecha del fichero',
  erp_extraction: 'fecha de extracción del ERP',
  load: 'fecha de carga',
};

// IND-019: «Con avisos» desaparece: un fichero publicado es «Actualizado»
// (también en cargas antiguas guardadas como warning); «Error» es «No publicado».
export const DAILY_STATUS_LABELS: Record<DailyLoadRunFile['status'], string> = {
  updated: 'Actualizado',
  unchanged: 'Sin cambios',
  warning: 'Actualizado',
  error: 'No publicado',
  not_loaded: 'No cargado',
};

export const DAILY_STATUS_COLORS: Record<DailyLoadRunFile['status'], 'success' | 'default' | 'error'> = {
  updated: 'success',
  unchanged: 'default',
  warning: 'success',
  error: 'error',
  not_loaded: 'default',
};

const MAX_EXAMPLES = 20;

export type DailyRunFileInput = {
  sourceKey: string;
  fileName: string;
  fileLastModified: number | null;
  outcomeKind: 'updated' | 'unchanged' | 'warning' | 'error' | null;
  previewStatus: string;
  previewError: string | null;
  confirmSummary: string | null;
  statusDetail: string;
  productionDateMax: string | null;
  unresolvedProductRefs: string[];
  productionConfirm: ProductionImportSummary | null;
  directInputsConfirm: DirectMaterialEntryImportConfirmResponse | null;
  ofConfirm: ImportedManufacturingOrderImportConfirmResponse | null;
  purchaseConfirm: PurchaseOrderImportConfirmResponse | null;
  // IND-019: datos del análisis para explicar un «No publicado».
  missingFieldLabels?: string[];
  purchaseRowErrors?: Array<Record<string, unknown>>;
  ofBlockingIssues?: ImportedManufacturingOrderImportBlockingIssue[];
  directInputsRowsNeedingReview?: number;
};

/** Día local (AAAA-MM-DD) de la fecha de modificación del fichero. */
export function localDay(timestamp: number | null): string | null {
  if (!timestamp || !Number.isFinite(timestamp)) return null;
  const value = new Date(timestamp);
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${value.getFullYear()}-${pad(value.getMonth() + 1)}-${pad(value.getDate())}`;
}

export function formatDataDay(value: string | null | undefined): string | null {
  const match = /^(\d{4})-(\d{2})-(\d{2})/.exec(value ?? '');
  return match ? `${match[3]}/${match[2]}/${match[1]}` : null;
}

function discard(
  code: string,
  label: string,
  count: number,
  examples: string[] = [],
): DailyLoadRunDiscard | null {
  if (!count || count <= 0) return null;
  return {
    code,
    label: label.slice(0, 255),
    count,
    examples: examples.slice(0, MAX_EXAMPLES).map((example) => example.slice(0, 255)),
  };
}

function rowText(row: Record<string, unknown>): string {
  const number = row.row_number ?? row.fila ?? row.source_row_number;
  const message = String(row.message ?? row.mensaje ?? '').trim();
  return number !== undefined && number !== null && number !== ''
    ? `fila ${String(number)}: ${message}`
    : message;
}

function productionDiscards(input: DailyRunFileInput): DailyLoadRunDiscard[] {
  const result = input.productionConfirm;
  if (!result) {
    return [
      discard(
        'producto_no_reconocido',
        'Producción descartada: producto no reconocido',
        input.unresolvedProductRefs.length,
        input.unresolvedProductRefs.map((ref) => `producto ${ref}`),
      ),
    ].filter((item): item is DailyLoadRunDiscard => item !== null);
  }
  return [
    discard(
      'producto_no_reconocido',
      'Producción descartada: producto no reconocido',
      result.rows_skipped_unresolved_product,
      (result.unresolved_product_refs ?? []).map((ref) => `producto ${ref}`),
    ),
    discard('articulo_pruebas', 'Líneas informativas (9999), no se cargan', result.rows_skipped_test_article ?? 0),
    discard(
      'filas_no_validas',
      'Producción descartada: filas no válidas',
      result.row_errors.length,
      result.row_errors.map((row) => `fila ${row.fila}: ${row.mensaje}`),
    ),
    discard(
      'errores_al_guardar',
      'Producción no guardada por error',
      result.persistence_errors.length,
      result.persistence_errors.map((row) => rowText(row as Record<string, unknown>)),
    ),
  ].filter((item): item is DailyLoadRunDiscard => item !== null);
}

function directInputsDiscards(input: DailyRunFileInput): DailyLoadRunDiscard[] {
  const result = input.directInputsConfirm;
  if (!result) return [];
  return [
    discard('material_no_reconocido', 'Albaranes de material no reconocido', result.rows_skipped_unresolved_material),
    discard('fecha_no_valida', 'Albaranes con fecha no válida', result.rows_skipped_invalid_date),
    discard('cantidad_no_valida', 'Albaranes con cantidad no válida', result.rows_skipped_invalid_quantity),
    discard('precio_no_valido', 'Albaranes con precio no válido', result.rows_skipped_invalid_unit_price),
    discard('lineas_ambiguas', 'Líneas ambiguas con lo ya registrado (se omiten)', result.rows_needing_review),
    // IND-013
    discard('abono_precio', 'Abono de precio, no afecta al stock', result.rows_skipped_price_credit ?? 0),
    discard('cantidad_cero', 'Albaranes con cantidad 0 (no es un movimiento)', result.rows_skipped_zero_quantity ?? 0),
    discard('articulo_pruebas', 'Líneas informativas (9999), no se cargan', result.rows_skipped_test_article ?? 0),
    discard(
      'errores_al_guardar',
      'Albaranes no guardados por error',
      result.persistence_errors.length,
      result.persistence_errors.map(rowText),
    ),
  ].filter((item): item is DailyLoadRunDiscard => item !== null);
}

export function isDuplicateOfRowError(row: Record<string, unknown>): boolean {
  return /duplicad|repetid/i.test(String(row.message ?? ''));
}

function ofDiscards(input: DailyRunFileInput): DailyLoadRunDiscard[] {
  const result = input.ofConfirm;
  if (!result) {
    return [
      discard(
        'producto_no_reconocido',
        'OF de producto no reconocido',
        input.unresolvedProductRefs.length,
        input.unresolvedProductRefs.map((ref) => `producto ${ref}`),
      ),
    ].filter((item): item is DailyLoadRunDiscard => item !== null);
  }
  const duplicates = result.row_errors.filter(isDuplicateOfRowError);
  // IND-017: los pedidos sin Nº OF con pendiente llegan como su propio descarte.
  const others = result.row_errors.filter(
    (row) => !isDuplicateOfRowError(row) && (row as Record<string, unknown>).type !== 'sin_of_con_pendiente',
  );
  return [
    discard(
      'of_repetida',
      'OF repetidas en el fichero (se usa la primera fila)',
      Math.max(result.rows_skipped_duplicate_in_file ?? 0, duplicates.length),
      duplicates.map((row) => {
        const order = String(row.manufacturing_order_number ?? '').trim();
        const number = row.row_number;
        return `OF ${order || '—'}${number !== undefined && number !== null ? ` (fila ${String(number)})` : ''}`;
      }),
    ),
    discard('filas_descartadas', 'Filas de OF descartadas', others.length, others.map(rowText)),
    discard(
      'errores_al_guardar',
      'OF no guardadas por error',
      result.persistence_errors.length,
      result.persistence_errors.map(rowText),
    ),
  ].filter((item): item is DailyLoadRunDiscard => item !== null);
}

function purchaseDiscards(input: DailyRunFileInput): DailyLoadRunDiscard[] {
  const result = input.purchaseConfirm;
  if (!result) return [];
  return [
    discard(
      'articulo_no_material',
      'Compras cuyo artículo no es un material',
      result.rows_unresolved_material,
    ),
    discard('articulo_pruebas', 'Líneas informativas (9999), no se cargan', result.rows_skipped_test_article ?? 0),
    discard('filas_no_validas', 'Filas de compras no válidas', result.row_errors.length, result.row_errors.map(rowText)),
  ].filter((item): item is DailyLoadRunDiscard => item !== null);
}

function dataDate(input: DailyRunFileInput): Pick<DailyLoadRunFile, 'data_date' | 'data_date_kind'> {
  const fileDay = localDay(input.fileLastModified);
  if (input.sourceKey === 'production') {
    const day = input.productionConfirm?.detected_date_max ?? input.productionDateMax;
    return day ? { data_date: day.slice(0, 10), data_date_kind: 'production' } : { data_date: null, data_date_kind: null };
  }
  if (input.sourceKey === 'imported-manufacturing-orders' && input.ofConfirm?.source_data_date) {
    return {
      data_date: input.ofConfirm.source_data_date.slice(0, 10),
      data_date_kind: input.ofConfirm.source_data_date_kind ?? 'file_modified',
    };
  }
  if (input.sourceKey === 'purchase-orders' && input.purchaseConfirm?.source_data_date) {
    return {
      data_date: input.purchaseConfirm.source_data_date.slice(0, 10),
      data_date_kind: input.purchaseConfirm.source_data_date_kind ?? 'file_modified',
    };
  }
  return fileDay
    ? { data_date: fileDay, data_date_kind: 'file_modified' }
    : { data_date: null, data_date_kind: null };
}

function confirmDiscards(input: DailyRunFileInput): ImportConfirmDiscard[] | null {
  const confirm =
    input.sourceKey === 'production'
      ? input.productionConfirm
      : input.sourceKey === 'direct-inputs'
        ? input.directInputsConfirm
        : input.sourceKey === 'imported-manufacturing-orders'
          ? input.ofConfirm
          : input.sourceKey === 'purchase-orders'
            ? input.purchaseConfirm
            : null;
  return Array.isArray(confirm?.discards) ? confirm.discards : null;
}

function itemText(item: NonNullable<ImportConfirmDiscard['items']>[number]): string {
  return [
    item.reference,
    item.detail,
    item.excel_row != null ? `fila ${item.excel_row}` : null,
  ].filter(Boolean).join(' · ');
}

/** IND-017: los descartes que devuelve el backend (con su nivel) mandan; se
 * completan con los que solo calcula esta pantalla. */
export function mergeDiscards(
  local: DailyLoadRunDiscard[],
  backend: ImportConfirmDiscard[] | null,
): DailyLoadRunDiscard[] {
  if (!backend) return local;
  const fromBackend = backend
    .filter((item) => item && item.count > 0)
    .map((item) => ({
      code: item.code,
      label: item.label.slice(0, 255),
      count: item.count,
      level: item.level ?? null,
      examples: (item.items ?? []).slice(0, MAX_EXAMPLES).map((entry) => itemText(entry).slice(0, 255)),
    }));
  const codes = new Set(fromBackend.map((item) => item.code));
  return [...fromBackend, ...local.filter((item) => !codes.has(item.code))];
}

const RELOAD_HINT = 'y vuelve a cargar la carpeta';

/** IND-019: motivo de un «No publicado» en una frase de negocio, con qué hacer. */
export function notPublishedReason(input: DailyRunFileInput): string {
  if (input.previewStatus === 'needs_mapping') {
    const fields = input.missingFieldLabels ?? [];
    return (fields.length > 0 ? `Faltan columnas obligatorias: ${fields.join(', ')}. ` : 'Faltan columnas obligatorias. ') +
      'Revisa que el fichero sea el del ERP.';
  }
  if (input.previewStatus === 'needs_review') {
    if (input.sourceKey === 'purchase-orders') {
      const count = input.purchaseRowErrors?.length ?? 0;
      return `${count} fila(s) de compras con errores: no se publica para no dejar pedidos incompletos. ` +
        `Corrígelas en el ERP ${RELOAD_HINT}.`;
    }
    if (input.sourceKey === 'imported-manufacturing-orders') {
      const issues = input.ofBlockingIssues ?? [];
      const kinds = Array.from(new Set(issues.map((issue) => issue.field_label || issue.message))).slice(0, 3);
      return `${issues.length} incidencia(s) en las OF impiden publicarlas` +
        (kinds.length > 0 ? ` (${kinds.join(' · ')})` : '') +
        `. Corrígelas en el ERP ${RELOAD_HINT}.`;
    }
    if (input.sourceKey === 'direct-inputs') {
      return `${input.directInputsRowsNeedingReview ?? 0} línea(s) de albaranes no cuadran con lo ya registrado. ` +
        `Revisa esos albaranes en el ERP ${RELOAD_HINT}.`;
    }
  }
  if (input.previewStatus === 'error' && input.previewError) {
    return `No se ha podido publicar: ${input.previewError.replace(/\.\s*$/, '')}. ` +
      'Vuelve a cargar la carpeta; si se repite, avisa a soporte.';
  }
  return `No se ha podido leer el fichero con su importador. Revisa que sea el del ERP ${RELOAD_HINT}.`;
}

/** IND-019: lo que explica un «No publicado» también va a «Revisa tus ficheros». */
function notPublishedDiscards(input: DailyRunFileInput, reason: string): DailyLoadRunDiscard[] {
  const inReview = input.previewStatus === 'needs_review';
  const purchaseErrors = inReview && input.sourceKey === 'purchase-orders' ? input.purchaseRowErrors ?? [] : [];
  const ofIssues = inReview && input.sourceKey === 'imported-manufacturing-orders' ? input.ofBlockingIssues ?? [] : [];
  return [
    discard('fichero_no_publicado', 'Fichero no publicado', 1, [reason]),
    discard(
      'compras_filas_rechazadas',
      'Filas de compras con errores (no se publica)',
      purchaseErrors.length,
      purchaseErrors.map(rowText),
    ),
    discard(
      'of_incidencias_bloqueantes',
      'Incidencias que impiden publicar las OF',
      ofIssues.length,
      ofIssues.map((issue) =>
        `OF ${issue.manufacturing_order_number ?? '—'} (fila ${issue.source_row_number ?? issue.row_number}): ${issue.message}`),
    ),
  ].filter((item): item is DailyLoadRunDiscard => item !== null);
}

/** IND-019: recuentos y fechas para la línea de resumen de la tarjeta. */
function loadResult(input: DailyRunFileInput): DailyLoadRunResult | null {
  if (input.sourceKey === 'production' && input.productionConfirm) {
    return {
      rows: input.productionConfirm.rows_imported,
      date_from: input.productionConfirm.detected_date_min?.slice(0, 10) ?? null,
      date_to: input.productionConfirm.detected_date_max?.slice(0, 10) ?? null,
    };
  }
  if (input.sourceKey === 'direct-inputs' && input.directInputsConfirm) {
    return {
      rows: input.directInputsConfirm.rows_imported,
      existing: input.directInputsConfirm.rows_skipped_existing,
      replaced: input.directInputsConfirm.rows_replaced ?? 0,
    };
  }
  if (input.sourceKey === 'imported-manufacturing-orders' && input.ofConfirm) {
    return { rows: input.ofConfirm.rows_imported };
  }
  if (input.sourceKey === 'purchase-orders' && input.purchaseConfirm) {
    return { rows: input.purchaseConfirm.rows_imported };
  }
  return null;
}

function shortDay(value: string | null | undefined): string | null {
  return formatDataDay(value)?.slice(0, 5) ?? null;
}

/** IND-019: «671 partes · del 01/10 al 23/10», «74 nuevos · 0 ya registrados ·
 * datos a 26/10», «529 OF · extracción 27/10», «68 líneas pendientes · datos a
 * 27/10». Null si no se publicó o es una carga guardada antes de IND-019. */
export function resultLine(runFile: DailyLoadRunFile): string | null {
  const result = runFile.result;
  if (runFile.status === 'error' || runFile.status === 'not_loaded') return null;
  const n = (value: number | null | undefined) => formatEsNumber(value ?? 0);
  const day = shortDay(runFile.data_date);
  const dataAt = day ? `datos a ${day}` : null;
  // Cargas guardadas antes de IND-019: solo la fecha de los datos.
  if (!result) return dataAt;
  let parts: Array<string | null>;
  if (runFile.source_key === 'production') {
    parts = [
      `${n(result.rows)} partes`,
      result.date_from && result.date_to
        ? `del ${shortDay(result.date_from)} al ${shortDay(result.date_to)}`
        : dataAt,
    ];
  } else if (runFile.source_key === 'direct-inputs') {
    parts = [
      `${n(result.rows)} nuevos`,
      `${n(result.existing)} ya registrados`,
      (result.replaced ?? 0) > 0 ? `${n(result.replaced)} sustituidos` : null,
      dataAt,
    ];
  } else if (runFile.source_key === 'imported-manufacturing-orders') {
    parts = [
      `${n(result.rows)} OF`,
      day && runFile.data_date_kind === 'erp_extraction' ? `extracción ${day}` : dataAt,
    ];
  } else {
    parts = [`${n(result.rows)} líneas pendientes`, dataAt];
  }
  return parts.filter(Boolean).join(' · ');
}

export function buildDailyRunFile(input: DailyRunFileInput): DailyLoadRunFile {
  const localDiscards =
    input.sourceKey === 'production'
      ? productionDiscards(input)
      : input.sourceKey === 'direct-inputs'
        ? directInputsDiscards(input)
        : input.sourceKey === 'imported-manufacturing-orders'
          ? ofDiscards(input)
          : input.sourceKey === 'purchase-orders'
            ? purchaseDiscards(input)
            : [];
  // «No cargado» solo es una copia omitida o una fuente que no estaba en la
  // carpeta; si se intentó y no se pudo publicar, es «No publicado» con su motivo.
  const status: DailyLoadRunFile['status'] =
    input.previewStatus === 'skipped'
      ? 'not_loaded'
      : input.outcomeKind ?? 'error';
  const loaded = status === 'updated' || status === 'unchanged' || status === 'warning';
  const reason = status === 'error' ? notPublishedReason(input) : null;
  const discards = [
    ...(reason ? notPublishedDiscards(input, reason) : []),
    ...mergeDiscards(localDiscards, confirmDiscards(input)),
  ];

  return {
    source_key: input.sourceKey,
    file_name: input.fileName.slice(0, 255),
    status,
    label: DAILY_STATUS_LABELS[status],
    summary: (
      reason ??
      (input.previewStatus === 'skipped'
        ? input.previewError ?? input.statusDetail
        : input.confirmSummary)
    )?.slice(0, 2000) ?? null,
    ...(loaded ? dataDate(input) : { data_date: null, data_date_kind: null }),
    discards: discards.slice(0, 20),
    result: loaded ? loadResult(input) : null,
  };
}

/** IND-019: «Carga completa: 4 de 4 ficheros actualizados» o «Carga
 * incompleta: N fichero(s) sin publicar» (No publicado y No cargado cuentan). */
export function dailyRunMessage(files: DailyLoadRunFile[]): string {
  const published = DAILY_LOAD_SOURCES.filter((key) => files.some((file) =>
    file.source_key === key &&
    (file.status === 'updated' || file.status === 'unchanged' || file.status === 'warning'))).length;
  const pending = DAILY_LOAD_SOURCES.length - published;
  return pending === 0
    ? `Carga completa: ${published} de ${DAILY_LOAD_SOURCES.length} ficheros actualizados`
    : `Carga incompleta: ${pending} fichero(s) sin publicar`;
}

/** Fuentes de la carga diaria que no estaban en la carpeta. */
export function missingDailySources(presentKeys: Iterable<string>): DailyLoadRunFile[] {
  const present = new Set(presentKeys);
  return DAILY_LOAD_SOURCES.filter((key) => !present.has(key)).map((key) => ({
    source_key: key,
    file_name: '',
    status: 'not_loaded',
    label: DAILY_STATUS_LABELS.not_loaded,
    summary: 'No estaba en la carpeta.',
    data_date: null,
    data_date_kind: null,
    discards: [],
  }));
}
